import { getTime, parseISO } from "date-fns";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useApi } from "../../../containers/ApiContainer";
import { useSnackbar } from "../../../containers/SnackbarContainer";
import { PlacementService } from "../../../services/PlacementService";
import { TripService } from "../../../services/TripService";
import { CsrStatus, PlacementWithLocation, TripWithTenant } from "../../../types";
import { isInfinityDate } from "../../../utils/dateHelpers";
import { csrStatusFilterColors } from "../../../utils/tripTypeHelpers";
import { ScheduleTimelinePresenter } from "./ScheduleTimelinePresenter";

interface ScheduleTimelineProps {
  trips: TripWithTenant[];
  onTripsChanged?: () => void;
  startDate: Date;
  endDate: Date;
  highlightTripId?: string;
  viewTimestamp?: Date;
  onTimeRangeChange?: (startDate: Date, endDate: Date) => void;
  inlineMode?: boolean;
  onTripSelect?: (tripId: string) => void;
  enableMultiSelect?: boolean;
  onSelectionChange?: (selectedIds: string[]) => void;
  shouldResetScroll?: boolean;
  onScrollReset?: () => void;
  onScrollChange?: (scrolledAway: boolean) => void;
}

export const ScheduleTimeline: React.FC<ScheduleTimelineProps> = ({
  trips,
  onTripsChanged,
  startDate,
  endDate,
  viewTimestamp,
  onTimeRangeChange,
  inlineMode = false,
  onTripSelect,
  enableMultiSelect = true,
  onSelectionChange,
  shouldResetScroll = false,
  onScrollReset,
  onScrollChange,
}) => {
  const { postgrest } = useApi();
  const snackbar = useSnackbar();

  // Modal state
  const [selectedTripId, setSelectedTripId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Multi-select state
  const [selectedTripIds, setSelectedTripIds] = useState<Set<string>>(
    new Set()
  );
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Placement state
  const [tripPlacements, setTripPlacements] = useState<
    Record<string, PlacementWithLocation[]>
  >({});
  const [placementsLoading, setPlacementsLoading] = useState(false);

  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<any>(null); // Reference to the timeline component

  // Cache and debouncing refs
  const placementCacheRef = useRef<Record<string, PlacementWithLocation[]>>({});
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchedTripsRef = useRef<string[]>([]);

  // Zoom state - default to 30 day view
  const [currentZoomLevel, setCurrentZoomLevel] = useState(1);
  const [visibleTimeStart, setVisibleTimeStart] = useState(getTime(startDate));
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(getTime(endDate));

  // Track the initial/default time range for scroll detection
  const initialTimeStartRef = useRef(getTime(startDate));
  const initialTimeEndRef = useRef(getTime(endDate));

  // Initialize services
  const placementService = useMemo(
    () => new PlacementService(postgrest),
    [postgrest]
  );

  const tripService = useMemo(() => new TripService(postgrest), [postgrest]);

  const selectedTrip = useMemo(
    () => trips.find((t) => t.trip_id === selectedTripId) || null,
    [trips, selectedTripId]
  );

  // Track shift key press and zoom shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsShiftPressed(true);
      }
      // Handle ESC to clear selection
      if (e.key === "Escape" && selectedTripIds.size > 0) {
        setSelectedTripIds(new Set());
        if (onSelectionChange) {
          onSelectionChange([]);
        }
      }
      // Handle Delete key for bulk delete
      if (e.key === "Delete" && selectedTripIds.size > 0 && enableMultiSelect) {
        setShowDeleteConfirmation(true);
      }
      // Handle zoom shortcuts
      if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        handleZoomIn();
      }
      if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        handleZoomOut();
      }
      // Reset zoom with Home key
      if (e.key === "Home") {
        e.preventDefault();
        handleZoomReset();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [
    selectedTripIds,
    enableMultiSelect,
    onSelectionChange,
    visibleTimeStart,
    visibleTimeEnd,
  ]);

  // Notify parent of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(Array.from(selectedTripIds));
    }
  }, [selectedTripIds, onSelectionChange]);

  // Reset scroll when requested
  useEffect(() => {
    if (shouldResetScroll) {
      // Reset to initial time range
      const newStart = getTime(startDate);
      const newEnd = getTime(endDate);

      setVisibleTimeStart(newStart);
      setVisibleTimeEnd(newEnd);
      setCurrentZoomLevel(1);

      // Update refs
      initialTimeStartRef.current = newStart;
      initialTimeEndRef.current = newEnd;

      // Notify parent that reset is complete
      if (onScrollReset) {
        onScrollReset();
      }
    }
  }, [shouldResetScroll, startDate, endDate, onScrollReset]);

  // Update initial refs when date range changes
  useEffect(() => {
    initialTimeStartRef.current = getTime(startDate);
    initialTimeEndRef.current = getTime(endDate);
  }, [startDate, endDate]);

  // Debounced placement fetcher
  const debouncedFetchPlacements = useCallback(
    (tripsToFetch: TripWithTenant[]) => {
      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(async () => {
        const tripIds = tripsToFetch.map((t) => t.trip_id).sort();
        const lastFetchedIds = lastFetchedTripsRef.current.sort();

        // Only fetch if trip IDs actually changed
        if (JSON.stringify(tripIds) === JSON.stringify(lastFetchedIds)) {
          return;
        }

        setPlacementsLoading(true);
        const placementsMap: Record<string, PlacementWithLocation[]> = {
          ...placementCacheRef.current,
        };

        // Only fetch placements for trips not in cache
        const tripsToFetchFromAPI = tripsToFetch.filter(
          (trip) => !placementCacheRef.current[trip.trip_id]
        );

        if (tripsToFetchFromAPI.length > 0) {
          try {
            await Promise.all(
              tripsToFetchFromAPI.map(async (trip) => {
                try {
                  const placements =
                    await placementService.getPlacementsWithLocation(
                      trip.trip_id
                    );
                  placementsMap[trip.trip_id] = placements;
                  placementCacheRef.current[trip.trip_id] = placements;
                } catch (error) {
                  console.error(
                    `Failed to fetch placements for trip ${trip.trip_id}:`,
                    error
                  );
                  placementsMap[trip.trip_id] = [];
                  placementCacheRef.current[trip.trip_id] = [];
                }
              })
            );
          } catch (error) {
            console.error("Error fetching placements:", error);
          }
        }

        // Update state with all placements for current trips
        const currentTripPlacements: Record<string, PlacementWithLocation[]> =
          {};
        tripsToFetch.forEach((trip) => {
          currentTripPlacements[trip.trip_id] =
            placementsMap[trip.trip_id] || [];
        });

        setTripPlacements(currentTripPlacements);
        lastFetchedTripsRef.current = tripIds;
        setPlacementsLoading(false);
      }, 100); // Reduced to 100ms for faster initial load
    },
    [placementService]
  );

  // Fetch placements for all trips with debouncing
  useEffect(() => {
    if (trips.length > 0) {
      debouncedFetchPlacements(trips);
    } else {
      setTripPlacements({});
      setPlacementsLoading(false);
    }

    // Cleanup on unmount
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [trips, debouncedFetchPlacements]);

  // Helper function to get current location for a trip
  const getTripLocation = useCallback(
    (
      tripId: string
    ): { name: string; type: "hangar" | "ramp" | "transient" } | null => {
      const placements = tripPlacements[tripId];
      if (!placements || placements.length === 0) {
        return { name: "Unassigned", type: "transient" };
      }

      // Get the first placement (assuming it's the current one)
      const currentPlacement = placements[0];
      return {
        name: currentPlacement.location_name || "Unassigned",
        type: currentPlacement.location_type || "transient",
      };
    },
    [tripPlacements]
  );

  // Helper function to determine if trip is fully assigned
  const isFullyAssigned = useCallback(
    (tripId: string): boolean => {
      const placements = tripPlacements[tripId];
      if (!placements || placements.length === 0) {
        return false;
      }

      // Check if any placement has actual hangar/ramp assignment
      return placements.some(
        (placement) =>
          placement.location_name &&
          placement.location_name !== "Unassigned" &&
          (placement.hangar_id || placement.ramp_id)
      );
    },
    [tripPlacements]
  );

  // Convert trips to timeline format
  const { groups, items, keys } = useMemo(() => {
    // Always create at least one empty group to maintain timeline structure
    if (trips.length === 0) {
      return {
        groups: [{ id: "empty", title: "No trips in this date range" }],
        items: [],
        keys: {
          groupIdKey: "id",
          groupTitleKey: "title",
          itemIdKey: "id",
          itemTitleKey: "title",
          itemDivTitleKey: "title",
          itemGroupKey: "group",
          itemTimeStartKey: "start",
          itemTimeEndKey: "end",
        },
      };
    }
    // Get unique tail numbers to create groups (rows)
    const tailNumberMap = new Map<string, string>();
    trips.forEach((trip) => {
      const tailNumber = trip.tail_number || "Unknown";
      if (!tailNumberMap.has(tailNumber)) {
        tailNumberMap.set(tailNumber, tailNumber);
      }
    });

    // Create groups (one per tail number)
    const groups = Array.from(tailNumberMap.keys()).map((tailNumber) => ({
      id: tailNumber,
      title: tailNumber,
      height: 50,
    }));

    // Create items (all trips)
    const items = trips.map((trip) => {
      // Initial background color (will be properly set in itemRenderer)
      let backgroundColor = "#949494"; // Default gray

      return {
        id: trip.trip_id,
        group: trip.tail_number || "Unknown",
        title: trip.notes || `[${trip.status.toUpperCase()}]`,
        start: getTime(parseISO(trip.arrival_date)),
        // Handle infinity departure (base tenants) - extend to right edge of timeline
        end: isInfinityDate(trip.departure_date)
          ? visibleTimeEnd
          : getTime(parseISO(trip.departure_date)),
        // Removed className to prevent CSS conflicts - colors are handled by inline styles
        itemProps: {
          "data-trip-id": trip.trip_id,
          "data-status": trip.status,
          style: {
            background: backgroundColor,
            borderTopWidth: trip.status === "active" ? 2 : 1,
            borderRightWidth: trip.status === "active" ? 2 : 1,
            borderBottomWidth: trip.status === "active" ? 2 : 1,
            borderLeftWidth: trip.status === "active" ? 2 : 1,
            borderStyle: "solid",
            borderColor:
              trip.status === "active" ? "#1976d2" : "rgba(0,0,0,0.1)",
            color: "#000",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
            zIndex: "auto",
            cursor: "pointer", // Show it's clickable, not draggable
          },
        },
        canMove: false, // Disable drag to move
        canResize: false, // Disable drag to resize
        canChangeGroup: false, // Don't allow moving between aircraft
      };
    });

    const keys = {
      groupIdKey: "id",
      groupTitleKey: "title",
      groupLabelKey: "title",
      groupRightTitleKey: "rightTitle",
      itemIdKey: "id",
      itemTitleKey: "title",
      itemDivTitleKey: "title",
      itemGroupKey: "group",
      itemTimeStartKey: "start",
      itemTimeEndKey: "end",
    };

    return { groups, items, keys };
  }, [trips, tripPlacements]); // Added tripPlacements to trigger re-render when placements load

  // Get border color based on CSR status (matches background color)
  const getBorderColor = useCallback(
    (trip: TripWithTenant, _tripId: string): string => {
      const csrStatus = (trip.csr_status || "requested") as CsrStatus;
      // Use the CSR status color for the border (same as fill, provides subtle definition)
      return csrStatusFilterColors[csrStatus] || csrStatusFilterColors.requested;
    },
    []
  );

  // Get border width based on status and assignment
  const getBorderWidth = useCallback(
    (trip: TripWithTenant, tripId: string): number => {
      if (trip.status === "active") {
        return 2; // Thick border for active
      }
      if (trip.status === "scheduled" && isFullyAssigned(tripId)) {
        return 2; // Thick blue border for assigned scheduled trips
      }
      if (trip.status === "scheduled" && !isFullyAssigned(tripId)) {
        return 1; // Thin border for unassigned scheduled trips
      }
      return 1; // Thin border for completed/cancelled/others
    },
    [isFullyAssigned]
  );

  // Handle item click
  const handleItemClick = useCallback(
    (itemId: string, e?: any) => {
      // Check if this is a shift+click for multi-select
      if (enableMultiSelect && (isShiftPressed || e?.shiftKey)) {
        // Toggle selection
        setSelectedTripIds((prev) => {
          const newSet = new Set(prev);
          if (newSet.has(itemId)) {
            newSet.delete(itemId);
          } else {
            newSet.add(itemId);
          }
          return newSet;
        });
      } else if (inlineMode && onTripSelect) {
        // In inline mode, just call the callback
        onTripSelect(itemId);
      } else {
        // In normal mode, open the modal
        if (selectedTripId === itemId && !modalOpen) {
          setModalOpen(true);
        } else {
          setSelectedTripId(itemId);
          setModalOpen(true);
        }
      }
    },
    [
      selectedTripId,
      modalOpen,
      inlineMode,
      onTripSelect,
      isShiftPressed,
      enableMultiSelect,
    ]
  );

  // Handle navigation to different trip
  const handleNavigate = useCallback((tripId: string) => {
    setSelectedTripId(tripId);
  }, []);

  // Handle modal close
  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    // Don't clear selectedTripId here to maintain the selection for re-clicking
  }, []);

  // Handle trip changes (refresh placements when trip is updated)
  const handleTripChanged = useCallback(async () => {
    // Clear cache for affected trips and refetch
    placementCacheRef.current = {};
    lastFetchedTripsRef.current = [];

    // Trigger parent to reload trip data
    if (onTripsChanged) {
      await onTripsChanged();
    }

    // After trip data is refreshed, refetch placements for new trip list
    if (trips.length > 0) {
      debouncedFetchPlacements(trips);
    }
  }, [trips, debouncedFetchPlacements, onTripsChanged]);

  // Handle timeline viewport changes (drag, zoom, etc.)
  const handleTimeChange = useCallback(
    (
      visibleTimeStart: number,
      visibleTimeEnd: number,
      updateScrollCanvas: any
    ) => {
      // Update visible time state
      setVisibleTimeStart(visibleTimeStart);
      setVisibleTimeEnd(visibleTimeEnd);

      // Check if we've scrolled away from initial position
      if (onScrollChange) {
        const hasScrolled =
          Math.abs(visibleTimeStart - initialTimeStartRef.current) > 60000 || // More than 1 minute difference
          Math.abs(visibleTimeEnd - initialTimeEndRef.current) > 60000;
        onScrollChange(hasScrolled);
      }

      // Convert milliseconds to Date objects
      const newStartDate = new Date(visibleTimeStart);
      const newEndDate = new Date(visibleTimeEnd);

      // Notify parent component of the time range change
      if (onTimeRangeChange) {
        onTimeRangeChange(newStartDate, newEndDate);
      }

      // Call the updateScrollCanvas function to update the timeline
      updateScrollCanvas(visibleTimeStart, visibleTimeEnd);
    },
    [onTimeRangeChange, onScrollChange]
  );

  // Zoom handler functions
  const handleZoomIn = useCallback(() => {
    const zoom = 1.5;
    const centerTime = (visibleTimeStart + visibleTimeEnd) / 2;
    const newHalfDuration = (visibleTimeEnd - visibleTimeStart) / 2 / zoom;

    // Check against minimum zoom (12 hours)
    const minZoom = 12 * 60 * 60 * 1000;
    if (newHalfDuration * 2 < minZoom) {
      return;
    }

    const newStart = centerTime - newHalfDuration;
    const newEnd = centerTime + newHalfDuration;

    setVisibleTimeStart(newStart);
    setVisibleTimeEnd(newEnd);
    setCurrentZoomLevel(currentZoomLevel * zoom);

    // Notify parent about scroll state change after zooming
    if (onScrollChange) {
      const hasScrolled =
        Math.abs(newStart - initialTimeStartRef.current) > 60000 || // More than 1 minute difference
        Math.abs(newEnd - initialTimeEndRef.current) > 60000;
      onScrollChange(hasScrolled);
    }
  }, [visibleTimeStart, visibleTimeEnd, currentZoomLevel, onScrollChange]);

  const handleZoomOut = useCallback(() => {
    const zoom = 0.75;
    const centerTime = (visibleTimeStart + visibleTimeEnd) / 2;
    const newHalfDuration = (visibleTimeEnd - visibleTimeStart) / 2 / zoom;

    // Check against maximum zoom (180 days)
    const maxZoom = 180 * 24 * 60 * 60 * 1000;
    if (newHalfDuration * 2 > maxZoom) {
      return;
    }

    const newStart = centerTime - newHalfDuration;
    const newEnd = centerTime + newHalfDuration;

    setVisibleTimeStart(newStart);
    setVisibleTimeEnd(newEnd);
    setCurrentZoomLevel(currentZoomLevel * zoom);

    // Notify parent about scroll state change after zooming
    if (onScrollChange) {
      const hasScrolled =
        Math.abs(newStart - initialTimeStartRef.current) > 60000 || // More than 1 minute difference
        Math.abs(newEnd - initialTimeEndRef.current) > 60000;
      onScrollChange(hasScrolled);
    }
  }, [visibleTimeStart, visibleTimeEnd, currentZoomLevel, onScrollChange]);

  const handleZoomReset = useCallback(() => {
    setVisibleTimeStart(getTime(startDate));
    setVisibleTimeEnd(getTime(endDate));
    setCurrentZoomLevel(1);
  }, [startDate, endDate]);

  // Sync visible time range with startDate/endDate props when they change
  useEffect(() => {
    setVisibleTimeStart(getTime(startDate));
    setVisibleTimeEnd(getTime(endDate));
    setCurrentZoomLevel(1); // Reset zoom level when date range changes
  }, [startDate, endDate]);

  // Handle bulk delete button click (shows confirmation)
  const handleBulkDeleteClick = useCallback(() => {
    if (selectedTripIds.size > 0) {
      setShowDeleteConfirmation(true);
    }
  }, [selectedTripIds]);

  // Handle bulk delete (actual deletion)
  const handleBulkDelete = useCallback(async () => {
    if (selectedTripIds.size === 0) return;

    try {
      const tripIdsArray = Array.from(selectedTripIds);

      // Delete each trip
      await Promise.all(
        tripIdsArray.map((tripId) => tripService.deleteTrip(tripId))
      );

      // Clear selection
      setSelectedTripIds(new Set());
      setShowDeleteConfirmation(false);

      // Show success message
      snackbar.notify({
        text: `Successfully deleted ${tripIdsArray.length} trip${
          tripIdsArray.length > 1 ? "s" : ""
        }`,
        severity: "success",
      });

      // Refresh trips
      if (onTripsChanged) {
        await onTripsChanged();
      }
    } catch (error) {
      console.error("Failed to delete trips:", error);
      snackbar.notify({
        text: "Failed to delete some trips. Please try again.",
        severity: "error",
      });
    }
  }, [selectedTripIds, tripService, snackbar, onTripsChanged]);

  // Handle cancel deletion
  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmation(false);
  }, []);

  // Clear selection handler for toolbar
  const handleClearSelection = useCallback(() => {
    setSelectedTripIds(new Set());
    if (onSelectionChange) {
      onSelectionChange([]);
    }
  }, [onSelectionChange]);

  return (
    <ScheduleTimelinePresenter
      trips={trips}
      groups={groups}
      items={items}
      keys={keys}
      selectedTrip={selectedTrip}
      modalOpen={modalOpen}
      placementsLoading={placementsLoading}
      startDate={startDate}
      endDate={endDate}
      viewTimestamp={viewTimestamp}
      timelineContainerRef={timelineContainerRef}
      timelineRef={timelineRef}
      handleItemClick={handleItemClick}
      handleModalClose={handleModalClose}
      handleNavigate={handleNavigate}
      handleTripChanged={handleTripChanged}
      handleTimeChange={handleTimeChange}
      getTripLocation={getTripLocation}
      getBorderColor={getBorderColor}
      getBorderWidth={getBorderWidth}
      inlineMode={inlineMode}
      // Zoom props
      visibleTimeStart={visibleTimeStart}
      visibleTimeEnd={visibleTimeEnd}
      currentZoomLevel={currentZoomLevel}
      handleZoomIn={handleZoomIn}
      handleZoomOut={handleZoomOut}
      handleZoomReset={handleZoomReset}
      // Multi-select props
      selectedTripIds={selectedTripIds}
      enableMultiSelect={enableMultiSelect}
      showDeleteConfirmation={showDeleteConfirmation}
      handleBulkDelete={handleBulkDelete}
      handleBulkDeleteClick={handleBulkDeleteClick}
      handleCancelDelete={handleCancelDelete}
      handleClearSelection={handleClearSelection}
    />
  );
};
